import Phaser from 'phaser';
import { CONSTANTS } from '../game.js';

export class ComboManager {
    constructor(scene) {
        this.scene = scene;
        this.comboCount = 0;
        this.maxComboCount = 0; // 最大コンボ数を追跡
        this.comboTimer = null;
        this.comboText = null;
        this.timerGraphics = null;
        this.remainingTime = 0;  // 残り時間を管理
        this.isComboActive = false;
        
        this.createComboText();
    }
    
    /**
     * コンボテキストの作成
     */
    createComboText() {
        this.comboText = this.scene.add.text(
            this.scene.game.config.width / 2,
            this.scene.game.config.height / 4, // 画面上部1/4の位置に配置
            '',
            {
                fontSize: '72px',
                fontStyle: 'bold',
                fontFamily: 'DotGothic16',
                fill: '#ffff00',
                stroke: '#000000',
                strokeThickness: 6,
                align: 'center'
            }
        );
        this.comboText.setOrigin(0.5);
        this.comboText.setAlpha(0.8); // デフォルトで半透明に設定
        this.comboText.setDepth(10); // 他の要素より背面に表示
    }
    
    /**
     * コンボを増加
     * @returns {number} 現在のコンボ倍率
     */
    incrementCombo() {
        this.comboCount++;
        this.isComboActive = true;
        
        // 最大コンボ数を更新
        if (this.comboCount > this.maxComboCount) {
            this.maxComboCount = this.comboCount;
        }
        
        // 現在の残り時間を取得（タイマーがない場合は0）
        const currentTime = this.comboTimer ? this.comboTimer.getRemaining() : 0;
        
        // 新しい時間を計算（現在の時間 + 延長時間）、最大値を超えないようにする
        this.remainingTime = Math.min(
            currentTime + CONSTANTS.COMBO.EXTENSION_MS,
            CONSTANTS.COMBO.MAX_TIMEOUT_MS
        );
        
        // コンボタイマーをリセット
        this.resetComboTimer();
        
        // コンボテキストの更新と表示
        this.updateComboText();
        
        return this.comboCount;
    }
    
    /**
     * コンボタイマーのリセット
     */
    resetComboTimer() {
        // 既存のタイマーをクリア
        if (this.comboTimer) {
            this.scene.time.removeEvent(this.comboTimer);
        }
        
        // 新しいタイマーを設定
        this.comboTimer = this.scene.time.delayedCall(
            this.remainingTime,
            this.breakCombo,
            [],
            this
        );
        
        // タイマー視覚化の更新
        this.updateTimerVisual();
    }
    
    /**
     * コンボタイマーの視覚化
     */
    updateTimerVisual() {
        // 既存のタイマー視覚化を削除
        if (this.timerGraphics) {
            this.timerGraphics.clear();
        } else {
            this.timerGraphics = this.scene.add.graphics();
            this.timerGraphics.setDepth(9); // コンボテキストの下に表示
        }
        
        // コンボが1以下の場合は表示しない
        if (this.comboCount <= 1) {
            return;
        }
        
        // プログレスバーの設定
        const centerX = this.scene.game.config.width / 2;
        const centerY = this.scene.game.config.height / 4 + 50; // コンボテキストの下に配置
        const barWidth = 200; // バーの全体の幅
        const barHeight = 6; // バーの高さ

        // 背景バー（グレー）の描画
        this.timerGraphics.lineStyle(0); // 線なし
        this.timerGraphics.fillStyle(0x333333, 0.2);
        this.timerGraphics.fillRect(centerX - barWidth / 2, centerY, barWidth, barHeight);

        // 残り時間に応じたプログレス（カラフル）
        const remainingTime = this.comboTimer ? this.comboTimer.getRemaining() : 0;
        const progress = remainingTime / CONSTANTS.COMBO.MAX_TIMEOUT_MS;

        // 残り時間に応じた色（緑→黄→赤）
        let color;
        if (progress > 0.6) {
            color = 0x00ff00; // 緑
        } else if (progress > 0.3) {
            color = 0xffff00; // 黄
        } else {
            color = 0xff0000; // 赤
        }

        // プログレスバーの描画（中央から左右に）
        const progressWidth = barWidth * progress;
        this.timerGraphics.fillStyle(color, 0.4);
        this.timerGraphics.fillRect(
            centerX - progressWidth / 2, // 中央から左右対称に
            centerY,
            progressWidth,
            barHeight
        );
    }
    
    /**
     * コンボテキストの更新
     */
    updateComboText() {
        if (this.comboCount <= 1) {
            this.comboText.setAlpha(0);
            return;
        }
        
        // COMBOの文字は不要で数字のみに
        this.comboText.setText(`${this.comboCount}`);
        this.comboText.setAlpha(0.8); // 半透明に設定
        
        // コンボ数に応じたテキストの色変更（桁の変化に合わせて）
        if (this.comboCount < 10) {
            this.comboText.setFill('#ffffff'); // 白色（1桁）
        } else if (this.comboCount < 100) {
            this.comboText.setFill('#ffff00'); // 黄色（2桁）
        } else if (this.comboCount < 1000) {
            this.comboText.setFill('#ff9900'); // オレンジ（3桁）
        } else {
            this.comboText.setFill('#ff0000'); // 赤（4桁以上）
        }
        
        // テキストアニメーション
        this.scene.tweens.add({
            targets: this.comboText,
            scale: { from: 1.5, to: 1 },
            duration: 300,
            ease: 'Bounce.Out'
        });
    }
    
    /**
     * コンボを破棄
     */
    breakCombo() {
        if (this.comboCount <= 1) return;
        
        // コンボ終了エフェクト
        this.showComboBreakEffect();
        
        // コンボリセット
        this.comboCount = 0;
        this.isComboActive = false;
        
        // テキストをフェードアウト
        this.scene.tweens.add({
            targets: this.comboText,
            alpha: 0,
            duration: 500,
            ease: 'Power2'
        });
        
        // タイマー表示をクリア
        if (this.timerGraphics) {
            this.timerGraphics.clear();
        }
    }
    
    /**
     * コンボ終了エフェクト
     */
    showComboBreakEffect() {
        // コンボが切れる視覚的エフェクト
        const centerX = this.scene.game.config.width / 2;
        const centerY = this.scene.game.config.height / 4; // コンボテキストと同じY座標
        
        // "COMBO BREAK!" テキスト
        const breakText = this.scene.add.text(
            centerX,
            centerY + 50,
            'BREAK!', // COMBOの文字を削除
            {
                fontSize: '24px',
                fontStyle: 'bold',
                fontFamily: 'DotGothic16',
                fill: '#ff0000',
                stroke: '#000000',
                strokeThickness: 4
            }
        );
        breakText.setOrigin(0.5);
        breakText.setDepth(11); // コンボテキストより前面に表示
        
        // テキストアニメーション
        this.scene.tweens.add({
            targets: breakText,
            scale: { from: 0.5, to: 1.5 },
            alpha: { from: 1, to: 0 },
            duration: 1000,
            ease: 'Power2',
            onComplete: () => {
                breakText.destroy();
            }
        });
        
        // 波紋エフェクト
        for (let i = 0; i < 3; i++) {
            const circle = this.scene.add.circle(
                centerX,
                centerY,
                50 + i * 20,
                0xff0000,
                0.7 - i * 0.2
            );
            circle.setStrokeStyle(2, 0xff0000);
            circle.setDepth(8); // コンボテキストの下に表示
            
            this.scene.tweens.add({
                targets: circle,
                scale: 2,
                alpha: 0,
                duration: 500 + i * 200,
                ease: 'Power2',
                onComplete: () => {
                    circle.destroy();
                }
            });
        }
    }
    
    /**
     * 現在のコンボ数を取得
     * @returns {number} 現在のコンボ数
     */
    getComboCount() {
        return this.comboCount;
    }
    
    /**
     * 最大コンボ数を取得
     * @returns {number} 最大コンボ数
     */
    getMaxComboCount() {
        return this.maxComboCount;
    }
    
    /**
     * コンボがアクティブかどうか
     * @returns {boolean} コンボがアクティブならtrue
     */
    isActive() {
        return this.isComboActive;
    }
    
    /**
     * 更新処理
     */
    update() {
        // コンボタイマーの視覚化を更新
        if (this.isComboActive && this.comboCount > 1) {
            this.updateTimerVisual();
        }
    }
    
    /**
     * リソースのクリーンアップ
     */
    cleanup() {
        if (this.comboTimer) {
            this.scene.time.removeEvent(this.comboTimer);
            this.comboTimer = null;
        }
        
        if (this.comboText) {
            this.comboText.destroy();
            this.comboText = null;
        }
        
        if (this.timerGraphics) {
            this.timerGraphics.destroy();
            this.timerGraphics = null;
        }
        
        // コンボ数と最大コンボ数をリセット
        this.comboCount = 0;
        this.maxComboCount = 0;
    }
}
